import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  alpha,
  Button,
  Modal,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Divider,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../theme';
import { useSupabase } from '../../contexts/SupabaseContext';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';
import { useTheme } from '@mui/material/styles';
import AmountField from '../../components/AmountField';
// style for  number input
const numberInputStyle = {
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};
// Colors for the pie chart
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28DF2',
  '#FF6B6B', '#4ECDC4', '#1A535C', '#FF9F1C', '#011627'
];

const Others = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-others', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemTransactions, setItemTransactions] = useState([]);
  
  // State for transaction actions
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Edit form state
  const [editPrice, setEditPrice] = useState('');
  const [editAmountInvested, setEditAmountInvested] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState(new Date());
  const [editUnits, setEditUnits] = useState('');
  const [editNotes, setEditNotes] = useState('');
  
  // Form state
  const [itemName, setItemName] = useState(null);
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [price, setPrice] = useState('');
  const [amountInvested, setAmountInvested] = useState('');
  const [units, setUnits] = useState('');
  const [notes, setNotes] = useState('');
  
  // User's saved item names (will be populated from existing investments)
  const [userItemNames, setUserItemNames] = useState([]);

  // Dummy data for Other Investments
  const dummyData = [
    { id: 1, name: 'Art Collection', value: 200000, units: 2, price: 100000, notes: 'Purchased from gallery' },
    { id: 2, name: 'Wine Investment', value: 150000, units: 10, price: 15000, notes: 'Vintage collection' },
    { id: 3, name: 'Collectibles', value: 75000, units: 5, price: 15000, notes: 'Rare items' },
  ];

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  // Function to fetch investments from Supabase
  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('Fetching other investments for user ID:', user.id);
      const { data, error } = await supabase
        .from('other_investments')
        .select('*')
        .eq('user_id', user.id)
        .order('purchase_date', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      console.log('Fetched other investments data:', data);
      
      if (data && data.length > 0) {
        // Transform the data to match the expected format for the component
        const formattedData = data.map(item => ({
          id: item.id,
          name: item.name,
          value: item.amount_invested,
          price: item.price,
          purchaseDate: item.purchase_date,
          units: item.units,
          notes: item.notes
        }));
        
        // Group investments by item name
        const groupedInvestments = groupInvestmentsByItem(formattedData);
        
        setInvestments(groupedInvestments);
        const total = formattedData.reduce((sum, item) => sum + item.value, 0);
        setTotalValue(total);
        console.log('Total Other Investments value:', total);
        
        // Extract unique item names from user's investments
        const uniqueItemNames = [...new Set(data.map(item => item.name))];
        setUserItemNames(uniqueItemNames);
      } else {
        console.log('No other investments found');
        setInvestments([]);
        setTotalValue(0);
        setUserItemNames([]);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
      // Fallback to dummy data in case of error
      const groupedInvestments = groupInvestmentsByItem(dummyData);
      setInvestments(groupedInvestments);
      setTotalValue(dummyData.reduce((sum, item) => sum + item.value, 0));
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);
  
  // Group investments by item name
  const groupInvestmentsByItem = (data) => {
    const groupedMap = data.reduce((acc, investment) => {
      if (!acc[investment.name]) {
        acc[investment.name] = {
          name: investment.name,
          totalValue: 0,
          totalUnits: 0,
          transactions: []
        };
      }
      
      acc[investment.name].totalValue += investment.value;
      acc[investment.name].totalUnits += parseFloat(investment.units || 0);
      acc[investment.name].transactions.push(investment);
      
      return acc;
    }, {});
    
    // Convert the grouped map to an array
    return Object.values(groupedMap).map((item, index) => ({
      id: index + 1,
      name: item.name,
      value: item.totalValue,
      units: item.totalUnits,
      transactions: item.transactions
    }));
  };
  
  // Initial data fetch and connection test
  useEffect(() => {
    const init = async () => {
      try {
        // Test Supabase connection
        console.log('Testing Supabase connection...');
        const { data: connectionTest, error: connectionError } = await supabase
          .from('other_investments')
          .select('count')
          .limit(1);
          
        if (connectionError) {
          console.error('Supabase connection error:', connectionError);
        } else {
          console.log('Supabase connection successful:', connectionTest);
        }
        
        // Fetch investments
        await fetchInvestments();
      } catch (error) {
        console.error('Initialization error:', error);
        setLoading(false);
      }
    };
    
    init();
  }, [fetchInvestments]);

  // Calculate amount when price or units changes
  useEffect(() => {
    if (price && units && parseFloat(price) > 0 && parseFloat(units) > 0) {
      setAmountInvested((parseFloat(price) * parseFloat(units)).toFixed(2));
    }
  }, [price, units]);

  // Calculate amount when price or units changes for edit modal
  useEffect(() => {
    if (editPrice && editUnits && parseFloat(editPrice) > 0 && parseFloat(editUnits) > 0) {
      setEditAmountInvested((parseFloat(editPrice) * parseFloat(editUnits)).toFixed(2));
    }
  }, [editPrice, editUnits]);

  const pieChartData = investments.map((item) => ({
    id: item.id,
    value: item.value,
    label: item.name,
  }));

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleReset = () => {
    setItemName(null);
    setPurchaseDate(new Date());
    setPrice('');
    setUnits('');
    setAmountInvested('');
    setNotes('');
  };

  const handleSave = async () => {
    if (!user) {
      alert('You must be logged in to save investments');
      return;
    }
    
    if (!itemName || !purchaseDate || !price || !units) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Get the item name (either from string input or from selected option)
      const itemNameValue = typeof itemName === 'string' ? itemName : itemName.label || itemName;
      
      // Prepare the data for Supabase - only include fields that exist in the schema
      const newInvestment = {
        name: itemNameValue,
        purchase_date: purchaseDate.toISOString().split('T')[0],
        price: parseFloat(price),
        amount_invested: parseFloat(amountInvested),
        units: parseFloat(units),
        notes: notes || null,
        user_id: user.id // Make sure to include user_id
      };

      console.log('Creating new investment:', newInvestment);

      // Insert data into Supabase
      const { data, error } = await supabase
        .from('other_investments')
        .insert([newInvestment])
        .select();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert response:', data);

      // Add the new investment to the list and update total value
      if (data && data.length > 0) {
        // Refresh the data from Supabase
        await fetchInvestments();
      }

      // Reset form and close modal
      handleReset();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving investment:', error);
      alert(`Failed to save investment: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransactionHistory = (item) => {
    setSelectedItem(item);
    setItemTransactions(item.transactions || []);
    setOpenHistoryModal(true);
  };
  
  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setSelectedItem(null);
    setItemTransactions([]);
  };

  const handleTransactionMenuOpen = (event, transaction) => {
    event.stopPropagation();
    setCurrentTransaction(transaction);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleTransactionMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditModalOpen = () => {
    handleTransactionMenuClose();
    
    if (!currentTransaction) {
      console.error('No transaction selected for editing');
      return;
    }
    
    console.log('Editing transaction:', currentTransaction);
    
    // Set form values with defaults in case of null/undefined values
    setEditPrice(currentTransaction.price ? currentTransaction.price.toString() : '');
    setEditUnits(currentTransaction.units ? currentTransaction.units.toString() : '');
    setEditPurchaseDate(currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate) : new Date());
    setEditNotes(currentTransaction.notes || '');
    // Amount invested will be calculated automatically based on price and units
  
    setOpenEditModal(true);
  };

  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setCurrentTransaction(null);
  };

  const handleDeleteDialogOpen = () => {
    handleTransactionMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleUpdateTransaction = async () => {
    if (!editPrice || !editUnits || !editPurchaseDate) {
      alert('Please fill all required fields');
      return;
    }

    if (!currentTransaction || !currentTransaction.id) {
      console.error('Missing transaction ID for update');
      alert('Cannot update transaction: Missing ID');
      return;
    }

    try {
      setLoading(true);
      
      // Calculate the amount invested based on units and price
      const calculatedAmount = parseFloat(editPrice) * parseFloat(editUnits);
      
      // Prepare the data for Supabase - ensure proper data types
      const updatedTransaction = {
        name: currentTransaction.name,
        purchase_date: editPurchaseDate instanceof Date 
          ? editPurchaseDate.toISOString().split('T')[0] 
          : new Date(editPurchaseDate).toISOString().split('T')[0],
        price: parseFloat(editPrice),
        amount_invested: calculatedAmount,
        units: parseFloat(editUnits),
        notes: editNotes || null
      };

      console.log('Updating transaction:', currentTransaction.id, updatedTransaction);

      // Update data in Supabase
      const { data, error } = await supabase
        .from('other_investments')
        .update(updatedTransaction)
        .eq('id', currentTransaction.id)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      console.log('Update response:', data);

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedItem && openHistoryModal) {
        // Get the latest transactions for this item
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('other_investments')
          .select('*')
          .eq('name', selectedItem.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError && refreshedTransactions) {
          const formattedTransactions = refreshedTransactions.map(item => ({
            id: item.id,
            name: item.name,
            value: item.amount_invested,
            price: item.price,
            purchaseDate: item.purchase_date,
            units: item.units,
            notes: item.notes
          }));
          
          setItemTransactions(formattedTransactions);
        }
      }

      // Close the edit modal
      handleEditModalClose();
      
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert(`Failed to update transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      setLoading(true);
      
      console.log('Deleting transaction:', currentTransaction.id);
      
      // Delete from Supabase
      const { error } = await supabase
        .from('other_investments')
        .delete()
        .eq('id', currentTransaction.id);

      if (error) {
        console.error('Supabase delete error:', error);
        throw error;
      }

      // Refresh the data from Supabase
      await fetchInvestments();
      
      // Also update the current transaction list in the modal if it's open
      if (selectedItem && openHistoryModal) {
        // Get the latest transactions for this item
        const { data: refreshedTransactions, error: refreshError } = await supabase
          .from('other_investments')
          .select('*')
          .eq('name', selectedItem.name)
          .order('purchase_date', { ascending: false });
          
        if (!refreshError) {
          if (refreshedTransactions && refreshedTransactions.length > 0) {
            const formattedTransactions = refreshedTransactions.map(item => ({
              id: item.id,
              name: item.name,
              value: item.amount_invested,
              price: item.price,
              purchaseDate: item.purchase_date,
              units: item.units,
              notes: item.notes
            }));
            
            setItemTransactions(formattedTransactions);
          } else {
            // No transactions left for this item, close the modal
            handleCloseHistoryModal();
          }
        }
      }

      // Close the delete dialog
      handleDeleteDialogClose();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(`Failed to delete transaction: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });

    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
        const bAllocation = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      }
      return 0;
    });

    setInvestments(sortedData);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/investments')}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box data-tour="inv-prod-summary">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Other Investments
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Total: {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                maximumFractionDigits: 0,
              }).format(totalValue)}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={fetchInvestments} 
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: alpha('#1976d2', 0.1) },
              }}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button 
            data-tour="inv-prod-add"
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2,
            }}
          >
            Add Investment
          </Button>
        </Box>
      </Box>

      {/* Modal for adding new investment */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-investment-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Add New Investment
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Autocomplete
                value={itemName}
                onChange={(event, newValue) => setItemName(newValue)}
                freeSolo
                autoHighlight
                options={userItemNames}
                getOptionLabel={(option) => {
                  // Handle both string values and objects
                  return typeof option === 'string' ? option : option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option => 
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  
                  // Add the user's input as an option if it's not already in the list
                  if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
                    filtered.push(params.inputValue);
                  }
                  
                  return filtered;
                }}
                groupBy={(option) => {
                  // Custom entered value
                  if (!userItemNames.includes(option)) {
                    return "Custom Entry";
                  }
                  // User's existing items
                  else {
                    return "Your Existing Investments";
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Investment Description" 
                    required 
                    fullWidth
                    placeholder="Enter or select investment description"
                    helperText="You can type a new description or select from your existing items"
                  />
                )}
              />
              
              <DatePicker
                label="Date of Purchase"
                value={purchaseDate}
                onChange={(newValue) => setPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Price per unit/share"
                sx={numberInputStyle}
              />
              
              <TextField
                label="Units"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                required
                fullWidth
                type="number"
                helperText="Number of units/shares"
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={amountInvested}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                fullWidth
                helperText="Auto-calculated (Price × Units)"
              />
              
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Additional details about this investment (optional)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Reset
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Save Investment
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Transaction Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleTransactionMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 150, borderRadius: 2 }
        }}
      >
        <MenuItem onClick={handleEditModalOpen} sx={{ gap: 1 }}>
          <EditIcon fontSize="small" color="primary" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ gap: 1 }}>
          <DeleteIcon fontSize="small" color="error" />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Transaction Modal */}
      <Modal
        open={openEditModal}
        onClose={handleEditModalClose}
        aria-labelledby="edit-transaction-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 500 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Edit Transaction
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                Investment: {currentTransaction?.name}
              </Typography>
              
              <DatePicker
                label="Date of Purchase"
                value={editPurchaseDate}
                onChange={(newValue) => setEditPurchaseDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              
              <TextField
                label="Price"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                required
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Price per unit/share"
                sx={numberInputStyle}
              />
              
              <TextField
                label="Units"
                value={editUnits}
                onChange={(e) => setEditUnits(e.target.value)}
                required
                fullWidth
                type="number"
                helperText="Number of units/shares"
                sx={numberInputStyle}
              />
              
              <AmountField
                label="Amount Invested"
                value={editAmountInvested}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                fullWidth
                helperText="Auto-calculated (Price × Units)"
              />
              
              <TextField
                label="Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Additional details about this investment (optional)"
              />
              
              <Divider />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleEditModalClose}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateTransaction}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Update Transaction
                </Button>
              </Box>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-transaction-dialog"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContentText>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Transaction Details:
              </Typography>
              <Typography variant="body2">
                Investment: {currentTransaction.name}<br />
                Date: {currentTransaction.purchaseDate ? new Date(currentTransaction.purchaseDate).toLocaleDateString('en-IN') : '-'}<br />
                Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(currentTransaction.value)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteDialogClose} 
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteTransaction} 
            variant="contained" 
            color="error"
            sx={{ borderRadius: 2, textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 4,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">No Other Investments Found</Typography>
          <Typography variant="body2">
            No investments found in this category. Start investing to see them here.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Add Your First Investment
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-prod-holdings"
              sx={{
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Investment Details
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Description
                        {sortConfig.key === 'name' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('value')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Value
                        {sortConfig.key === 'value' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Allocation
                        {sortConfig.key === 'allocation' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ width: 80 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => (
                      <TableRow
                        key={investment.id}
                        onClick={() => handleOpenTransactionHistory(investment)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2,
                              }}
                            />
                            {investment.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0,
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {((investment.value / totalValue) * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View transactions">
                            <IconButton size="small" sx={{ color: 'primary.main' }}>
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4} data-tour="inv-prod-charts">
            <Paper
              sx={{
                height: '100%',
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Distribution
                </Typography>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                }}
              >
                {pieChartData.length > 0 && (
                  <PieChart colors={chartPalette}
                    series={[
                      {
                        data: pieChartData,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                        // Hide labels and blocks
                        label: false,
                        labelLine: false,
                      },
                    ]}
                    height={450}
                    // Hide legend and adjust margins
                    legend={{
                      hidden: true
                    }}
                    margin={{
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Transaction History Modal */}
      <Modal
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        aria-labelledby="transaction-history-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 700 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: { xs: 2.5, sm: 4 },
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                {selectedItem?.name}
              </Typography>
              {/* --- Begin: Average Price Row (styled like others) --- */}
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'primary.main' }}
              >
                Invested Amount: {new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR',
                  maximumFractionDigits: 0,
                }).format(selectedItem?.value || 0)} |
                Total Units: {(selectedItem?.units || 0).toFixed(2)} |
                Average Price: ₹{(() => {
                  if (!itemTransactions || itemTransactions.length === 0) return '0.00';
                  const totalAmount = itemTransactions.reduce((sum, tx) => sum + tx.value, 0);
                  const totalUnits = itemTransactions.reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0);
                  return totalUnits > 0 ? (totalAmount / totalUnits).toFixed(2) : '0.00';
                })()}
              </Typography>
              {/* --- End: Average Price Row --- */}
            </Box>
            <IconButton onClick={handleCloseHistoryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Chip 
              icon={<ReceiptIcon fontSize="small" />} 
              label="Transaction History" 
              color="primary" 
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Date</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Price (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount (₹)</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>Units</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Notes</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 60 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itemTransactions.length > 0 ? (
                  itemTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {transaction.purchaseDate 
                          ? new Date(transaction.purchaseDate).toLocaleDateString('en-IN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.price 
                          ? transaction.price.toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(transaction.value)}
                      </TableCell>
                      <TableCell align="right">
                        {transaction.units 
                          ? parseFloat(transaction.units).toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {transaction.notes || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleTransactionMenuOpen(e, transaction)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No transaction history available
                    </TableCell>
                  </TableRow>
                )}
                {itemTransactions.length > 0 && (
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(itemTransactions.reduce((sum, tx) => sum + tx.value, 0))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {itemTransactions
                        .reduce((sum, tx) => sum + parseFloat(tx.units || 0), 0)
                        .toFixed(2)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleCloseHistoryModal}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <GuidedTour steps={TOURS['inv-others']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default Others;